import {
  isCfaRentOrLease,
  msaTotalCFAInstallment,
  msaTotalCfaInstallmentIncPayout,
  theMsaInstalmentFrequency,
  theMsaPayableMethod,
  theMsaTerm,
  validateDataFromOriginator,
  validateNonNegativeFinanceFields,
  validateRequiredFinanceFields,
  recommendServiceCopyCostBw,
  recommendServiceCopyCostColour,
} from "../../redux/selectors";
import {
  cfaCcBwAgreedTotalClickVolume,
  cfaBaseCharge,
  cfaCcBwTotalCostExclGst,
  cfaCcBwExcessTotalCostExclGst,
  cfaCcColourAgreedTotalClickVolume,
  cfaCcColourTotalCostExclGst,
  cfaCcColourExcessTotalCostExclGst,
  isMsaInstalmentFrequencyValid,
  isMsaPayableMethodValid,
  isMsaTermValid,
  theMsaInstallmentValue,
  validateIfFinanceConfigured,
  totalMinimumCharge
} from "./finance";
import { formatMoney } from "accounting-js";
import { uniq } from "lodash-es";
import { HARDWARE_DISCOUNT, SOFTWARE_DISCOUNT, SAAS_DISCOUNT, OPPORTUNITY_TYPES_NO_DISCOUNT, IS_NZ, CFNZ_FINANCE_TERM, OPPORTUNITY_TYPES_NZ_GOVERNMENT } from "../../constants";

export * from "./finance";

export const quoteItemCount = state => state.quote.items.length;

export const topLevelHardwareItems = state =>
  state.quote.items.filter(item => item.isHardware);

export const topLevelHardwareItemsWithClickCharge = state =>
  state.quote.items.filter(item => item.defaultCcBw || item.defaultCcColour );

export const topLevelSoftwareItems = state =>
  state.quote.items.filter(item => item.isSoftware);

export const topLevelSaasItems = state =>
  state.quote.items.filter(item => item.isSaas);

export const hasSoftware = state =>
  state.quote.items.some(
    item =>
      item.isSoftware ||
      (item.options &&
        item.options
          .flatMap(({ selections }) => selections)
          .some(selection => selection.isSoftware))
  );

export const hasSaas = state =>
  state.quote.items.some(
    item =>
      item.isSaas ||
      (item.options &&
        item.options
          .flatMap(({ selections }) => selections)
          .some(selection => selection.isSaas))
  );

const flatPriceItems = state => {
  const simpleItems = state.quote.items
    .filter(item => !item.options)
    .map(({ price, qty, excludeFromMarginSplit, isSoftware, isHardware, isSaas, subscriptionTerm }) => ({
      price,
      qty,
      excludeFromMarginSplit,
      isSoftware,
      isHardware,
      isSaas,
      subscriptionTerm
    }));

  const itemsInBundle = state.quote.items
    .filter(item => item.options)
    .flatMap(item =>
      item.options.flatMap(option =>
        option.selections.flatMap(
          ({ price, targetPrice, qty, excludeFromMarginSplit, isSoftware, isHardware, isSaas }) => ({
            price,
            targetPrice,
            qty: qty * item.qty,
            excludeFromMarginSplit,
            isSoftware,
            isHardware,
            isSaas,
            subscriptionTerm: item.subscriptionTerm
          })
        )
      )
    );

  return simpleItems.concat(itemsInBundle);
};

const total = items =>
  items.reduce((total, item) => total + item.price * item.qty, 0);

const saasTotal = items =>
  items.reduce((total, item) => total + item.price * item.qty * item.subscriptionTerm, 0);

//Items which has hardware maintenance charge which is the same as flatMonthlyCharge
export const FmcItems = state => state.quote.items
  .filter(item => item.flatMonthlyCharge)
  .map(({ flatMonthlyCharge, qty, name}) => ({
    flatMonthlyCharge, qty, name
  }));

export const flatMonthlyChargeTotal = state => FmcItems(state).reduce((total, row) => {
    let amount = parseFloat(row.flatMonthlyCharge * row.qty);
    return total + (isNaN(amount) ? 0 : amount);
  }, 0);

export const CashSaasItemsTargetPriceTotal = state => {
  const result = state.quote.items.filter(item => item.isSaas && item.saasFinanceType === 'Cash')
  .reduce((total, row) => {
    let amount = parseFloat(row.saasTargetPrice)
    return total + (isNaN(amount) ? 0 : amount)
  }, 0)
  return result;
}

export const saasTargetTotal = items => {
  return items.reduce((total, item) => total + item.targetPrice * item.qty * item.subscriptionTerm, 0);
}

const flatHardwarePriceItems = state =>
  flatPriceItems(state).filter(item => item.isHardware);

const flatSoftwarePriceItems = state =>
  flatPriceItems(state).filter(item => item.isSoftware);

export const flatSaasPriceItems = state =>
  flatPriceItems(state).filter(item => item.isSaas);

const fixedHardwareTotal = state =>
  total(
    flatHardwarePriceItems(state).filter(item => item.excludeFromMarginSplit)
  );

const fixedSoftwareTotal = state =>
  total(
    flatSoftwarePriceItems(state).filter(item => item.excludeFromMarginSplit)
  );

export const mspHardwareTotal = state => total(flatHardwarePriceItems(state));

export const mspSoftwareTotal = state => total(flatSoftwarePriceItems(state));

export const mspSaasTotal = state => saasTotal(flatSaasPriceItems(state));

export const effectiveMspHardwareTotal = state =>
  mspHardwareTotal(state) - fixedHardwareTotal(state);

export const effectiveMspSoftwareTotal = state =>
  mspSoftwareTotal(state) - fixedSoftwareTotal(state);

const effectiveTargetHardwareTotal = state =>
  state.quote.targetHardwareTotal - fixedHardwareTotal(state);

const effectiveTargetSoftwareTotal = state =>
  state.quote.targetSoftwareTotal - fixedSoftwareTotal(state);

export const targetPrice = (
  state,
  item,
  contextItems = [],
  isGovernmentOpportunity = false
) => {
  // The target price rolling up only applies to
  // hardware items for government opportunity.
  const hardwareItems = contextItems.filter(
    contextItem => contextItem.isHardware
  );
  const rollupTargetHardwareItem = hardwareItems.find(
    item => item.includesMainUnit
  );
  const normalHardwareItems = hardwareItems.filter(
    item => item !== rollupTargetHardwareItem
  );

  let floatPrice, fixedPrice;
  if (
    isGovernmentOpportunity &&
    rollupTargetHardwareItem &&
    item === rollupTargetHardwareItem
  ) {
    floatPrice = hardwareItems
      .filter(item => !item.excludeFromMarginSplit)
      .reduce((sum, { price, qty }) => sum + price * qty, 0);
    fixedPrice = hardwareItems
      .filter(item => item.excludeFromMarginSplit)
      .reduce((sum, { price, qty }) => sum + price * qty, 0);
  } else if (
    isGovernmentOpportunity &&
    rollupTargetHardwareItem &&
    normalHardwareItems.includes(item)
  ) {
    floatPrice = 0;
    fixedPrice = 0;
  } else if (item.excludeFromMarginSplit) {
    floatPrice = 0;
    fixedPrice = item.price;
  } else {
    floatPrice = item.price;
    fixedPrice = 0;
  }

  const effectiveMspTotal = item.isSoftware
    ? effectiveMspSoftwareTotal(state)
    : effectiveMspHardwareTotal(state);
  const effectiveTargetTotal = item.isSoftware
    ? effectiveTargetSoftwareTotal(state)
    : effectiveTargetHardwareTotal(state);
  const ratio = effectiveMspTotal
    ? effectiveTargetTotal / effectiveMspTotal
    : 1;

  return floatPrice * ratio + fixedPrice;
};

export const mspTotal = state =>
  Number((mspHardwareTotal(state) + mspSoftwareTotal(state) + mspSaasTotal(state)).toFixed(2));

export const targetTotal = state =>
  Number((state.quote.targetHardwareTotal + state.quote.targetSoftwareTotal + state.quote.targetSaasTotal).toFixed(2));

export const targetTotalCash = state => {
  var totalHardware = 0;
  var totalSoftware = 0;
  var totalSaas = 0;
  if(state.quote.finance.financeType.toLowerCase() === "cash")
  {
    //get total hardware price from hardware or Software bundles
    totalHardware += calculateTargetTotal(getHardwareItems(state.quote))

    //get total software price from hardware or Software bundles
    totalSoftware += calculateTargetTotal(getSoftwareItems(state.quote))
  }

  //add up the SaaS selections based on financeType
  getSaasItems(state.quote).forEach(item => {
    item.options
        .flatMap(option => option.selections)
        .forEach(selection => {
          switch (selection.financeType.toLowerCase())
          {
            case "cash":
              if(selection.isHardware) {
                totalHardware += (selection.targetPrice * selection.qty)
              }
              if(selection.isSoftware) {
                totalSoftware += (selection.targetPrice * selection.qty)
              }
              if(selection.isSaas) {
                totalSaas += (selection.targetPrice * selection.qty * item.subscriptionTerm)
              }
              break;
            default: //do nothing
          }
        })
  })
  return Number((totalHardware + totalSoftware + totalSaas).toFixed(2));
}

export const targetTotalFinance = state => {
  var totalHardware = 0;
  var totalSoftware = 0;
  var totalSaas = 0;
  if(state.quote.finance.financeType.toLowerCase() !== "cash")
  {
    //get total hardware price from hardware or Software bundles
    totalHardware += calculateTargetTotal(getHardwareItems(state.quote))

    //get total software price from hardware or Software bundles
    totalSoftware += calculateTargetTotal(getSoftwareItems(state.quote))
  }

  //add up the SaaS selections based on financeType
  getSaasItems(state.quote).forEach(item => {
    item.options
        .flatMap(option => option.selections)
        .forEach(selection => {
          switch (selection.financeType.toLowerCase())
          {
            case "cash": //do nothing
              break;
            default:
              if(selection.isHardware) {
                totalHardware += (selection.targetPrice * selection.qty)
              }
              if(selection.isSoftware) {
                totalSoftware += (selection.targetPrice * selection.qty)
              }
              if(selection.isSaas) {
                totalSaas += (selection.targetPrice * selection.qty * item.subscriptionTerm)
              }
              break;
          }
        })
  })
  return Number((totalHardware + totalSoftware + totalSaas).toFixed(2));
}

const getHardwareItems = quote => {
  //get all hardware selections from non-saas bundles, as the finance will match the quote finance
  const bundleItems = quote.items.filter(item => !item.isSaas && item.type === "bundle").flatMap(item => item.options.flatMap(option => option.selections.filter(selection => selection.isHardware)).map(fm => { return {...fm, bundleQty: item.qty} }))

  //get all hardware simple items
  const simpleItems = quote.items.filter(item => item.type === "simple" && item.isHardware === true).map(fm => { return {...fm, bundleQty: 1} })
  return bundleItems.concat(simpleItems)
}

const getSoftwareItems = quote => {
  //get all software selections from non-saas bundles, as the finance will match the quote finance
  const bundleItems = quote.items.filter(item => !item.isSaas && item.type === "bundle").flatMap(item => item.options.flatMap(option => option.selections.filter(selection => selection.isSoftware)).map(fm => { return {...fm, bundleQty: item.qty} }))

  //get all software simple items
  const simpleItems = quote.items.filter(item => item.type === "simple" && item.isSoftware === true).map(fm => { return {...fm, bundleQty: 1} })
  return bundleItems.concat(simpleItems)
}

const getSaasItems = (quote) =>
  //get all SaaS items from quote
  quote.items.filter(item => item.isSaas)

const calculateTargetTotal = selections =>
  //for array of non-saas selections only
  selections.reduce((sum, { targetPrice, qty, bundleQty }) => sum + targetPrice * qty * bundleQty, 0)


const msaPriceFlagSet = state =>
  new Set(
    state.quote.items
      .map(item => item.msaPriceFlag)
      .filter(flag => Boolean(flag))
  );

export const isMsaPriceFlagValid = state =>
  state.quote.isMsa && msaPriceFlagSet(state).size === 1;

export const theMsaPriceFlag = state => state.quote.isMsa && isMsaPriceFlagValid(state)
    ? msaPriceFlagSet(state)
      .values()
      .next().value
    : "";

export const validPriceFlagList = ({ quote }) => quote.validPriceFlagList ? quote.validPriceFlagList : [];

export const isAllItemsWarranty = ({ quote }) =>
  quote.items.filter( p => !p.isSoftware).every( i => i.hasOwnProperty('underWarranty') && i.underWarranty );

export const isQuoteNameEmpty = state => !state.quote.name;

export const validateQuoteName = state =>
  isQuoteNameEmpty(state) ? "Quote Name cannot be empty." : "";

export const isTargetHardwareTotalValid = state => {
  //SPARK-1417 - just keeping the code by returning true if business want the valiation back in the future
  return true;

  return OPPORTUNITY_TYPES_NO_DISCOUNT.includes(state.opportunity.OpportunityType)
  ? state.quote.targetHardwareTotal === Math.round(mspHardwareTotal(state) * 100) / 100
  : state.quote.targetHardwareTotal >= mspHardwareTotal(state) * HARDWARE_DISCOUNT
}

export const validateHardwareTotal = state => {
  if(!isTargetHardwareTotalValid(state))
  {
    return OPPORTUNITY_TYPES_NO_DISCOUNT.includes(state.opportunity.OpportunityType)
    ? 'Total Hardware cannot be changed for this opportunity type'
    : `Total Hardware should be no less than ${HARDWARE_DISCOUNT * 100}% Total HardWare MSP`;
  }
  return "";
}

export const isTargetSoftwareTotalValid = state => {
  //SPARK-1417 - just keeping the code by returning true if business want the valiation back in the future
  return true;

  return OPPORTUNITY_TYPES_NO_DISCOUNT.includes(state.opportunity.OpportunityType)
  ? state.quote.targetSoftwareTotal === Math.round(mspSoftwareTotal(state) * 100) / 100
  : state.quote.targetSoftwareTotal >= mspSoftwareTotal(state) * SOFTWARE_DISCOUNT;
}

export const validateSoftwareTotal = state => {
  if(!isTargetSoftwareTotalValid(state))
  {
    return OPPORTUNITY_TYPES_NO_DISCOUNT.includes(state.opportunity.OpportunityType)
    ? 'Total Software cannot be changed for this opportunity type'
    : `Total Software should be no less than ${SOFTWARE_DISCOUNT * 100}% of Total Software MSP`;
  }
  return "";
}

export const isTargetSaasTotalValid = state =>
OPPORTUNITY_TYPES_NO_DISCOUNT.includes(state.opportunity.OpportunityType)
? state.quote.targetSaasTotal === mspSaasTotal(state)
: state.quote.targetSaasTotal >= mspSaasTotal(state) * SAAS_DISCOUNT;

export const validateSoftwareAddresses = state =>
  hasSoftware(state) &&
  (!state.quote.softwareDeliveryAddressId ||
    !state.quote.softwareInstallationAddressId)
    ? `Software addresses are required`
    : "";

const validateQuoteItemMsaOptions = state => {
  const messages = state.quote.isMsa
    ? state.quote.items.flatMap(
        ({ msaDefaultTerm, msaDefaultCfa, msaDefaultMdsCost, name }) =>
          [
            {
              isValid: msaDefaultTerm > 0 || !isCfaRentOrLease(state),
              label: "Term"
            },
            {
              isValid: msaDefaultCfa > 0 || !isCfaRentOrLease(state),
              label: "CF per thousand"
            }
          ]
            .filter(({ isValid }) => !isValid)
            .map(
              ({ label }) => `
Product ${name}:
The MSA account-specific "${label}" is not configured.
Please contact the product marketing team for the configuration.
Once the product configuration has been updated in Spark admin,
please remove the affected product from the quote and re-add.`
            )
      )
    : [];

  // Validate MSA Price Flag on each quote item
  if (state.quote.isMsa && !isMsaPriceFlagValid(state)) {
    const list = state.quote.items
      .map(({ sku, msaPriceFlag }) => `${sku}: ${msaPriceFlag === 'CPQ_N/A' ? 'Not Applicable' : msaPriceFlag}`)
      .join(", ");

    messages.push(
      `The configured MSA Price Flags are not the same in the quote: ${list}.
 Please request a fix to the product configuration in Admin, then remove the affected product from your quote. You can then re-add it to the quote to get the updated price flag.`
    );
  }
  // Validate MSA Term on each quote item
  if (!isMsaTermValid(state)) {
    const list = state.quote.items
      .map(({ sku, msaTerm }) => `${sku}: ${msaTerm}`)
      .join(", ");

    messages.push(
      `The target MSA terms are not the same one in the quote: ${list}.
 Please remove the affected product from your quote, then re-add it to the quote with the same MSA Term.`
    );
  }

  // Validate MSA Payable Method on each quote item
  if (!isMsaPayableMethodValid(state)) {
    const list = state.quote.items
      .map(({ sku, msaPayable }) => `${sku}: ${msaPayable}`)
      .join(", ");

    messages.push(
      `The configured MSA Payable Methods are not the same in the quote: ${list}.
 Please request a fix to the product configuration in Admin, then remove the affected product from your quote. You can then re-add it to the quote to get the MSA Payable Methods.`
    );
  }

  // Validate MSA Installment Frequency on each quote item
  if (!isMsaInstalmentFrequencyValid(state)) {
    const list = state.quote.items
      .map(
        ({ sku, msaInstalmentFrequency }) => `${sku}: ${msaInstalmentFrequency}`
      )
      .join(", ");

    messages.push(
      `The configured MSA Installment Frequency are not the same in the quote: ${list}.
 Please request a fix to the product configuration in Admin, then remove the affected product from your quote. You can then re-add it to the quote to get the updated MSA Installment Frequency.`
    );
  }

  // Validate MSA Click Charges (Metro & Regional)
  const invalidItems = state.quote.items
    .filter(
      ({
        name,
        isRegional,
        msaMetroClickChargeBw,
        msaMetroClickChargeColour,
        msaRegionalClickChargeBw,
        msaRegionalClickChargeColour
      }) =>
        (msaMetroClickChargeBw ||
          msaMetroClickChargeColour ||
          msaRegionalClickChargeBw ||
          msaRegionalClickChargeColour) &&
        !isRegional.every(regional =>
          regional
            ? msaRegionalClickChargeBw || msaRegionalClickChargeColour
            : msaMetroClickChargeBw || msaMetroClickChargeColour
        )
    )
    .map(({ name }) => name)
    .join(", ");
  if (invalidItems) {
    messages.push(`
The MSA account specific Metro/Regional Click Charges values are not configured.
Please contact the product marketing team for the configurations.
The invalid quote items are ${invalidItems}.
    `);
  }

  return messages;
};

export const isTonerRecycleBoxValid = state => {
  if(!IS_NZ) return true
  return (state.quote.tonerRecycleBox !== null && ["0","1"].includes(state.quote.tonerRecycleBox))
}

export const validateRecycleBox = state => {
  if(!IS_NZ) { return ""}
  return (state.quote.tonerRecycleBox !== null && ["0","1"].includes(state.quote.tonerRecycleBox)) ? "" : "Please select Yes or No for Toner Recycle Box"
}

export const isServiceTermValid = state => {
  if(state.quote.serviceTerm === null) return false;
  if(isNaN(state.quote.serviceTerm)) return false;
  if(state.quote.finance.financeType !== "CASH" && state.quote.serviceTerm !== state.quote.finance.financeTerm) return false;
  if(state.quote.finance.financeType === "CASH" && state.quote.serviceTerm < CFNZ_FINANCE_TERM.min) return false;
  if(state.quote.finance.financeType === "CASH" && state.quote.serviceTerm > CFNZ_FINANCE_TERM.max) return false;
  return true;
}

export const validateServiceTerm = state => {
  if(state.quote.serviceTerm === null) return "Service Term is invalid. Please update";
  if(isNaN(state.quote.serviceTerm)) return "Service Term is invalid. Please update";
  if(state.quote.finance.financeType !== "CASH" && state.quote.serviceTerm !== state.quote.finance.financeTerm) return "Service Term must match Finance Term";
  if(state.quote.finance.financeType === "CASH" && state.quote.serviceTerm < CFNZ_FINANCE_TERM.min) return `Service Term must be between ${CFNZ_FINANCE_TERM.min} and ${CFNZ_FINANCE_TERM.max}`;
  if(state.quote.finance.financeType === "CASH" && state.quote.serviceTerm > CFNZ_FINANCE_TERM.max) return `Service Term must be between ${CFNZ_FINANCE_TERM.min} and ${CFNZ_FINANCE_TERM.max}`;
  return "";
}

export const isFinanceTermValid = state => {
  if(state.quote.finance.financeType === "CASH") { return true }
  if(state.quote.finance.financeTerm === null) return false;
  if(isNaN(state.quote.finance.financeTerm)) return false;
  if(state.quote.finance.financeTerm - state.quote.finance.deferredPayment < 12) return false
  if(state.quote.finance.financeTerm - state.quote.finance.deferredPayment > 60) return false
  return true
}

export const validateFinanceTerm = state => {
  if(state.quote.finance.financeType === "CASH") { return "" }
  if(state.quote.finance.financeTerm === null) return "Finance Term is invalid. Please update";
  if(isNaN(state.quote.finance.financeTerm)) return "Finance Term is invalid. Please update";
  if(state.quote.finance.financeTerm - state.quote.finance.deferredPayment < 12) return "Finance Term minus any deferred payment must be at least 12 months"
  if(state.quote.finance.financeTerm - state.quote.finance.deferredPayment > 60) return "Finance Term minus any deferred payment must not be greater than 60 months"
  return ""
}

export const validateSaasAddresses = state =>
  hasSaas(state) &&
  (!state.quote.saasDeliveryAddressId ||
    !state.quote.saasInstallationAddressId)
    ? `SaaS addresses are required`
    : "";

export const validateSaasSubscriptionTerm = state => {
  var errorMessage = "";
  state.quote.items.filter(item => item.isSaas)
  .forEach(item => {
    if(state.quote.finance.financeType === "CASH" && item.subscriptionTerm > 60) {
      errorMessage = `When paying by Cash, SaaS Subscription Term (${item.subscriptionTerm} Months) must not be greater than 60`
    }
    if(state.quote.finance.financeType === "CASH" && item.subscriptionTerm !== state.quote.serviceTerm) {
      errorMessage = `SaaS Subscription Term (${item.subscriptionTerm} Months) must equal the quote Service Term (${state.quote.serviceTerm} Months). Please update the quote Service Term, or edit the SaaS bundle and update the Subscription Term`
    }
    else if(state.quote.finance.financeType !== "CASH" && item.subscriptionTerm !== (state.quote.finance.financeTerm)) {
      errorMessage = `SaaS Subscription Term (${item.subscriptionTerm} Months) must equal the quote Finance Term (${state.quote.finance.financeTerm} Months). Please update the quote Finance Term, or edit the SaaS bundle and update the Subscription Term`
    }
  })
  return errorMessage;
}

export const validateServiceCopyCost = state => {
  if(!["PMP"].includes(state.quote.finance.financeSubtype)){
    return "";
  }

  const errorMessage = "Please review and update Originator Service Copy Cost";
  const rSccBw = recommendServiceCopyCostBw(state);
  const sccBw = state.quote.finance.cfaCcBwServiceCopyCost;
  if(rSccBw > sccBw){
    return errorMessage;
  }

  const rSccColour = recommendServiceCopyCostColour(state);
  const sccColour = state.quote.finance.cfaCcColourServiceCopyCost;
  if(rSccColour > sccColour){
    return errorMessage;
  }
  return "";
}

export const validationMessages = state =>
  uniq(
    [
      validateQuoteName(state),
      validateHardwareTotal(state),
      validateSoftwareTotal(state),
      validateSoftwareAddresses(state),
      validateIfFinanceConfigured(state),
      ...validateRequiredFinanceFields(state),
      ...validateNonNegativeFinanceFields(state),
      ...validateDataFromOriginator(state),
      ...validateQuoteItemMsaOptions(state),
      ...state.quote.itemValidationMap.flatMap(item => item.messages),
      validateRecycleBox(state),
      validateServiceTerm(state),
      validateFinanceTerm(state),
      validateSaasAddresses(state),
      validateSaasSubscriptionTerm(state),
      validateServiceCopyCost(state)
    ].filter(message => Boolean(message))
  );

// The return quote object does not contain the "status" attribute
export const quoteForBackend = state => {
  const { opportunity, quote } = state;
  const { finance } = quote;
  return {
    //region ID & Name
    oid: opportunity.ID,
    qid: quote.qid || undefined,
    quote_name: quote.name,

    customer_group_code: quote.customerGroupCode,
    customer_group_id: quote.customerGroupId,

    opportunity_type: quote.opportunityType,
    tender_code: quote.tenderCode,

    is_msa: quote.isMsa,
    sfdc_customer_number: quote.sfdcCustomerNumber,
    is_toner_recycle: IS_NZ ? quote.tonerRecycleBox : null,
    service_term: quote.serviceTerm,
    //endregion

    //region Totals
    price_flag: quote.priceFlag,
    is_primary: quote.isPrimary,
    total_software_subtotal: Math.round(mspSoftwareTotal(state) * 100) / 100,
    total_hardware_subtotal: Math.round(mspHardwareTotal(state) * 100) / 100,
    total_saas_subtotal: mspSaasTotal(state),
    subtotal: mspTotal(state),
    total_software: quote.targetSoftwareTotal,
    total_hardware: quote.targetHardwareTotal,
    total_saas: quote.targetSaasTotal,
    target_price: targetTotal(state),
    total_tmc: totalMinimumCharge(state),
    //endregion

    //region Finance
    finance_type: finance.financeType,
    payout_info: finance.payoutInfo,
    ...(finance.financeType === "CFA"
      ? {
          //region Common Finance Fields
          finance_subtype: finance.financeSubtype,
          deferred_payment: finance.deferredPayment,
          //endregion

          ...(quote.isMsa
            ? {
                // MSA Only
                canon_finance_payout: finance.canonFinancePayout,
                third_party_payout: finance.thirdPartyPayout,
                finance_term: theMsaTerm(state),
                instalment_amount: theMsaInstallmentValue(state),
                instalment_type: theMsaInstalmentFrequency(state),
                payable_in: theMsaPayableMethod(state)
              }
            : {
                // Non-MSA, the quote has to go through originator
                originator_reference: finance.originatorReference,

                cfa_amount: finance.cfaAmount,
                total_amount_financed: finance.totalAmountFinanced,

                canon_finance_payout: finance.canonFinancePayout,
                third_party_payout: finance.thirdPartyPayout,

                finance_term: finance.financeTerm,
                instalment_amount: finance.instalmentAmount,
                instalment_type: finance.instalmentType,
                payable_in: finance.payableIn
              }),

          // Non-MSA: Lease Only, MSA: lease and rental
          ...(finance.financeSubtype === "LEASE" ||
          (quote.isMsa && finance.financeSubtype === "RENTAL")
            ? { residual_value: finance.residualValue }
            : null),

          //region PPP Only
          ...(["PPP_STANDARD", "PPP_COMBINED", "PMP"].includes(finance.financeSubtype)
            ? {
                adjustment_period: finance.adjustmentPeriod,
                canon_copy_plan_type: finance.canonCopyPlanType,
                cfa_base_charge: Number((cfaBaseCharge(state) / 100).toFixed(2)) || 0,

                cfa_cc_b_w: finance.cfaCcBw,
                cfa_cc_b_w_adjust_click_charge_per_month_excl_gst:
                  finance.cfaCcBwAdjustClickChargePerMonthExclGst,
                cfa_cc_b_w_minimum_monthly_click_volume:
                  finance.cfaCcBwMinimumMonthlyClickVolume,
                cfa_cc_b_w_service_copy_cost: finance.cfaCcBwServiceCopyCost,
                cfa_cc_b_w_total_cost_excl_gst: cfaCcBwTotalCostExclGst(state),
                cfa_cc_b_w_agreed_total_click_volume: cfaCcBwAgreedTotalClickVolume(
                  state
                ),
                cfa_cc_b_w_excess_financed_copy_cost: finance.cfaCcBwExcessFinancedCopyCost,
                cfa_cc_b_w_excess_total_cost_excl_gst: cfaCcBwExcessTotalCostExclGst(state),

                cfa_cc_colour: finance.cfaCcColour,
                cfa_cc_colour_adjust_click_charge_per_month_excl_gst:
                  finance.cfaCcColourAdjustClickChargePerMonthExclGst,
                cfa_cc_colour_minimum_monthly_click_volume:
                  finance.cfaCcColourMinimumMonthlyClickVolume,
                cfa_cc_colour_service_copy_cost:
                  finance.cfaCcColourServiceCopyCost,
                cfa_cc_colour_total_cost_excl_gst: cfaCcColourTotalCostExclGst(
                  state
                ),
                cfa_cc_colour_agreed_total_click_volume: cfaCcColourAgreedTotalClickVolume(
                  state
                ),
                cfa_cc_colour_excess_financed_copy_cost: finance.cfaCcColourExcessFinancedCopyCost,
                cfa_cc_colour_excess_total_cost_excl_gst: cfaCcColourExcessTotalCostExclGst(state),
              }
            : null)
          //endregion
        }
      : null),
    //endregion

    ...(finance.financeType === "CFNZ"
      ? {
          //region Common Finance Fields
          finance_subtype: finance.financeSubtype,
          deferred_payment: finance.deferredPayment,
          //endregion


          // Non-MSA, the quote has to go through originator (later, for now they are user-entered)
          //NOTE, was advised that currently NZ has no "MSA", and also no PPP so these options are no added for CFNZ
          originator_reference: finance.originatorReference,

          cfa_amount: finance.cfaAmount,
          total_amount_financed: finance.totalAmountFinanced,

          canon_finance_payout: finance.canonFinancePayout,
          third_party_payout: finance.thirdPartyPayout,

          finance_term: finance.financeTerm,
          instalment_amount: finance.instalmentAmount,
          instalment_type: finance.instalmentType,
          payable_in: finance.payableIn,

          ...(finance.financeSubtype === "LEASE"
            ? { residual_value: finance.residualValue }
            : null),

        }
      : null),
    //endregion

    //region Software Addresses and Billing Address
    ...(hasSoftware(state)
      ? {
          sw_delivery_address_id: quote.softwareDeliveryAddressId,
          sw_installation_address_id: quote.softwareInstallationAddressId
        }
      : null),

    //region SaaS Addresses and Billing Address
    ...(hasSaas(state)
      ? {
          saas_delivery_address_id: quote.saasDeliveryAddressId,
          saas_installation_address_id: quote.saasInstallationAddressId
        }
      : null),
    customer_billing_address_id: quote.customerBillingAddressId,
    //endregion

    items: quote.items.map(
      ({
        purchaseOrders,
        ccBw,
        defaultCcBw,
        userClickVolumeBw,
        ccCampaigns,
        ccColour,
        defaultCcColour,
        userClickVolumeColour,
        costCenters,
        deliveryAddressId,
        excludeFromMarginSplit,
        flatMonthlyCharge,
        installationAddressId,
        isRegional,
        isSoftware,
        isHardware,
        isSaas,
        key,
        msaCfa,
        msaDefaultCfa,
        msaDefaultMdsCost,
        msaDefaultTerm,
        msaInstalmentFrequency,
        msaMdsCost,
        msaPayableMethod,
        msaPriceFlag,
        msaMetroClickChargeBw,
        msaMetroClickChargeColour,
        msaRegionalClickChargeBw,
        msaRegionalClickChargeColour,
        msaTerm,
        name,
        options,
        price,
        saasTargetPrice,
        saasMSP,
        productId,
        qty,
        scanCharge,
        scanningMonthlyCharge,
        sku,
        subscriptionTargetChargeValue,
        subscriptionTerm,
        subscriptionUserNum,
        targetPrice,
        tradeIns,
        type,
        underWarranty,
        perA3Charge,
        tonerDeliveryCharge,
        minimumMonthlyCharge,
        nzScanningMonthlyCharge,
        existingTenantLabel,
        existingTenantValue,
        saasFinanceType
      }) => ({
        //region Common Fields
        is_software: isSoftware,
        is_hardware: isHardware,
        is_saas: isSaas,
        cc_campaigns: ccCampaigns,
        exclude_from_margin_split: excludeFromMarginSplit,
        key,
        name,
        price: isSaas ? price + saasMSP : price,
        product_id: productId,
        qty,
        sku,
        target_price: isSaas ? targetPrice + saasTargetPrice : targetPrice,
        type,
        saas_identifier_label: existingTenantLabel || null,
        saas_identifier_value: existingTenantLabel ? existingTenantValue : null,
        finance_type: saasFinanceType || null,
        //endregion

        //region Bundle Options
        options:
          options &&
          options.map(({ optionId, selections }) => ({
            option_id: optionId,
            selections: selections.map(
              ({
                includesMainUnit,
                isSoftware,
                isHardware,
                isSaas,
                name,
                price,
                productId,
                qty,
                selectionId,
                sku,
                targetPrice,
                financeType,
              }) => ({
                includes_main_unit: includesMainUnit,
                is_software: isSoftware,
                is_hardware: isHardware,
                is_saas: isSaas,
                name,
                price,
                product_id: productId,
                qty,
                selection_id: selectionId,
                sku,
                target_price: targetPrice,
                subscription_term: isSaas ? subscriptionTerm : null,
                finance_type: financeType
              })
            )
          })),
        //endregion

        //region Extra Options
        cc_b_w: ccBw,
        cc_colour: ccColour,
        default_cc_b_w: defaultCcBw,
        default_cc_colour: defaultCcColour,
        user_click_volume_bw: userClickVolumeBw,
        user_click_volume_colour: userClickVolumeColour,
        flat_monthly_charge: flatMonthlyCharge,
        scan_charge: scanCharge,
        nz_minimum_monthly_charge: minimumMonthlyCharge,
        nz_scanning_monthly_charge: nzScanningMonthlyCharge,
        nz_toner_delivery_charge: tonerDeliveryCharge,
        nz_per_a3_charge: perA3Charge,
        scanning_monthly_charge: scanningMonthlyCharge,
        subscription_target_charge_value: subscriptionTargetChargeValue,
        subscription_term: subscriptionTerm,
        subscription_user_num: subscriptionUserNum,
        under_warranty: underWarranty,
        //endregion

        //region MSA Options
        ...(quote.isMsa && type === "bundle"
          ? {
              msa_price_flag: msaPriceFlag,
              msa_payable_method: msaPayableMethod,
              msa_instalment_frequency: msaInstalmentFrequency,
              default_mds_cost: msaDefaultMdsCost,
              target_mds_cost: msaMdsCost,

              // region: Always send MSA options to backend so we can restore them later no matter if it's CF or Cash deals
              // These are all unit value; quantity is not considered here
              default_term: msaDefaultTerm,
              target_term: msaTerm,
              default_cfa_cost_per_thousand: msaDefaultCfa,
              target_cfa_cost_per_thousand: msaCfa,
              total_cfa_installment: msaTotalCFAInstallment(
                targetPrice,
                msaCfa
              ),
              total_installment_cost_third_party_payout: msaTotalCfaInstallmentIncPayout(
                state,
                targetPrice,
                msaCfa
              ),

              msa_metro_click_charge_bw: msaMetroClickChargeBw,
              msa_metro_click_charge_colour: msaMetroClickChargeColour,
              msa_regional_click_charge_bw: msaRegionalClickChargeBw,
              msa_regional_click_charge_colour: msaRegionalClickChargeColour
              // endregion
            }
          : null),
        //endregion

        //region Per unit options
        delivery_address_id: deliveryAddressId,
        installation_address_id: installationAddressId,
        is_regional: isRegional,
        purchase_orders: purchaseOrders,
        cost_centers: costCenters,
        trade_ins: tradeIns
        //endregion
      })
    )
  };
};

export const isGovernmentOpportunity = ({ quote }) => {
  if(IS_NZ) {
    return OPPORTUNITY_TYPES_NZ_GOVERNMENT.includes(quote.opportunityType)
  }
  else {
    return quote.opportunityType === "GOVERNMENT";
  }
}

export const doesQuoteMatchOpportunity = ({ quote, opportunity }) =>
  !quote.qid ||
  (opportunity.OpportunityType === quote.opportunityType &&
    opportunity.TenderCode === quote.tenderCode &&
    opportunity.IsMSA === quote.isMsa &&
    opportunity.CustomerNumber === quote.sfdcCustomerNumber);
